/* ---------------------------------------------------------------------------
   District rate tables — what a unit of work costs, where, and on whose
   authority.

   `QC-06`, and the half of it that this repository cannot supply.

   THE DECISION THIS IMPLEMENTS. Asked which rate source to price against, the
   answer was **divisional district rates, approved annually**. So the identity
   of a rate table is (district, fiscal year), it carries the date and the office
   that approved it, and every figure the application prints names the table it
   came out of. A cost with no table behind it is not a cost this register will
   show.

   THE NORMS SIT BEHIND THE RATE, NOT BESIDE IT. *Norms for Rate Analysis of Road
   and Bridge Works, 2075* (Cabinet approval 2075-04-25, first amendment issued
   by DoR's Quality Research and Development Centre) gives, per work item, the
   labour man-days, materials and equipment required. A district rate IS that
   analysis carried out against local wages and material rates — which is exactly
   why the rate is divisional and annual. So a row here carries the norm item it
   was analysed from (`normRef`) and the resulting rate, and the estimate prints
   both. Re-deriving the analysis in this application would mean holding a second
   copy of the norms and a wage table, and producing a number that disagreed with
   the Division's own approved rate.

   **THIS SHIPS WITH NO RATES, AND THAT IS THE CORRECT STATE.** There is no
   defensible way for this repository to invent a Nepali unit rate. The interim
   norms for routine bridge maintenance in the guideline (Part 3, §11) are a
   scanned table this project cannot read as data, and the district rates are
   approved per division per year in offices this code has never seen. So the
   engine is complete, checked, and empty — and `estimate.ts` refuses to produce
   a figure rather than defaulting to one. `loadRateTable` is how a Division's
   own table gets in.

   That refusal is the same answer `Not screened` and `Not recorded` give. A
   register that answers "about four hundred thousand rupees" about work it has
   no rate for is worse than one that says it has no rate: the first is a number
   somebody puts in a budget.

   NO FISCAL-YEAR ARITHMETIC. A rate table declares its own fiscal year as a
   string — `2081/82` — and nothing here converts a Gregorian date into one. The
   Bikram Sambat year boundary falls in mid-July and shifts against the Gregorian
   calendar, and a conversion written from memory would silently file a table
   under the wrong year for a fortnight every summer. What the application can
   honestly measure is how long ago the table was approved, which is
   `daysSinceApproval` below.
   --------------------------------------------------------------------------- */

import { treatmentFor, TREATMENT_RULES, worded, type TreatmentWording } from './remedial.ts';

export interface RateRow {
  /** Treatment id from src/data/remedial.ts. What ties a rate to a recommendation. */
  treatment: string;
  /** Unit the rate is per. Checked against the treatment's own unit — see validateRateTable. */
  unit: string;
  /** Rupees per unit, as the Division approved it. */
  rate: number;
  /**
   * The item in *Norms for Rate Analysis of Road and Bridge Works, 2075* this
   * rate was analysed from.
   *
   * Required, not optional. A rate with no norm behind it is a number somebody
   * typed, and the estimate has no way to say so afterwards. If a Division
   * prices an item the norms do not cover — which §1.3 of the guideline's Part 3
   * anticipates — the reference should say that in as many words rather than be
   * left empty.
   */
  normRef: string;
}

export interface RateTable {
  /** District the Division approved it for. Matches `Bridge.district`. */
  district: string;
  /** Nepali fiscal year, as written on the approval, e.g. `2081/82`. */
  fiscalYear: string;
  /** ISO date of approval. */
  approvedOn: string;
  /** The office that approved it. Printed on every estimate built from it. */
  approvedBy: string;
  /**
   * Contingency, per cent, as a named line rather than folded into the rates.
   *
   * Baked into a rate it becomes invisible and gets applied twice the first time
   * somebody compares this estimate with one prepared by hand. `estimate.ts`
   * prints it as its own line.
   */
  contingencyPct?: number;
  /** VAT, per cent. Same rule: its own line, never inside a rate. */
  vatPct?: number;
  rows: readonly RateRow[];
}

/**
 * Every treatment a rate table could carry a row for.
 *
 * Only the costable ones. A Major-class treatment has no rate by construction —
 * DoR's own programme document says its estimate comes after an investigation
 * and a design — so a table offering one would be pricing work this application
 * has already refused to price.
 */
export function ratableTreatments(wording?: TreatmentWording): { id: string; unit: string; label: string }[] {
  const seen = new Map<string, { id: string; unit: string; label: string }>();
  for (const rule of TREATMENT_RULES) {
    const t = worded(rule.treatment, wording);
    if (t.quantityKey === null) continue;
    if (!seen.has(t.id)) seen.set(t.id, { id: t.id, unit: t.unit, label: t.label });
  }
  return [...seen.values()];
}

/**
 * What is wrong with a rate table, as a list. Empty means it is usable.
 *
 * A LIST RATHER THAN THE FIRST FAULT, because this is what a Division sees after
 * uploading a spreadsheet. Reporting one bad row at a time turns a ten-minute
 * job into ten round trips, and the person doing it is not the person who wrote
 * this code.
 */
export function validateRateTable(t: RateTable): string[] {
  const faults: string[] = [];

  if (!t.district.trim()) faults.push('The table names no district.');
  if (!/^\d{4}\/\d{2}$/.test(t.fiscalYear)) {
    faults.push(`Fiscal year "${t.fiscalYear}" is not in the form 2081/82.`);
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t.approvedOn) || Number.isNaN(Date.parse(t.approvedOn))) {
    faults.push(`Approval date "${t.approvedOn}" is not an ISO date.`);
  }
  if (!t.approvedBy.trim()) {
    faults.push('The table does not say which office approved it. Every estimate prints this.');
  }

  const known = new Map(ratableTreatments().map((r) => [r.id, r]));
  const seen = new Set<string>();

  for (const row of t.rows) {
    const expect = known.get(row.treatment);
    if (!expect) {
      faults.push(`"${row.treatment}" is not a treatment this register can cost.`);
      continue;
    }
    if (seen.has(row.treatment)) {
      faults.push(`"${row.treatment}" is priced twice — one of the two would win silently.`);
    }
    seen.add(row.treatment);

    /* The unit is checked rather than trusted, and this is the check that earns
       its keep. A rate entered per square metre against a treatment billed per
       metre produces an estimate wrong by the width of the repair, in the right
       currency, with a plausible total. */
    if (row.unit !== expect.unit) {
      faults.push(
        `"${row.treatment}" is billed in ${expect.unit} but the rate is per ${row.unit}.`,
      );
    }
    if (!Number.isFinite(row.rate) || row.rate <= 0) {
      faults.push(`"${row.treatment}" has a rate of ${row.rate}, which is not a price.`);
    }
    if (!row.normRef.trim()) {
      faults.push(`"${row.treatment}" names no norm reference.`);
    }
  }

  for (const pct of [t.contingencyPct, t.vatPct]) {
    if (pct !== undefined && (!Number.isFinite(pct) || pct < 0 || pct > 100)) {
      faults.push(`A percentage of ${pct} is not one.`);
    }
  }

  return faults;
}

/** The rate for one treatment in one table, or null where the table does not price it. */
export function rateFor(table: RateTable, treatmentId: string): RateRow | null {
  return table.rows.find((r) => r.treatment === treatmentId) ?? null;
}

/**
 * How old the approval is, in days, against a date the caller supplies.
 *
 * The caller supplies "today" rather than this reading a clock, so a check can
 * pin the answer. Rates are approved annually, so anything past about a year is
 * a table that has been superseded somewhere and not here — `RATE_TABLE_STALE_DAYS`
 * is where the estimate starts saying so.
 */
export function daysSinceApproval(table: RateTable, todayIso: string): number | null {
  const then = Date.parse(table.approvedOn);
  const now = Date.parse(todayIso);
  if (Number.isNaN(then) || Number.isNaN(now)) return null;
  return Math.floor((now - then) / 86_400_000);
}

/**
 * When an annually-approved table stops being this year's.
 *
 * 400 rather than 365. The approval date moves within the year and a table
 * approved late in one fiscal year is still the current one well past its own
 * anniversary; crying stale on day 366 would mark most tables stale most of the
 * time, and a warning that is usually wrong is one people learn to ignore.
 */
export const RATE_TABLE_STALE_DAYS = 400;

/**
 * The rate tables this build ships with.
 *
 * Deliberately empty. See the header: there is no honest way for this repository
 * to supply a Nepali district rate, and an application that shipped plausible
 * ones would have them quoted back at it in a budget submission.
 */
export const BUILT_IN_RATE_TABLES: readonly RateTable[] = [];

/** The table for a district and fiscal year, out of whatever has been loaded. */
export function findRateTable(
  tables: readonly RateTable[],
  district: string,
  fiscalYear?: string,
): RateTable | null {
  const forDistrict = tables.filter((t) => t.district === district);
  if (!forDistrict.length) return null;
  if (fiscalYear) return forDistrict.find((t) => t.fiscalYear === fiscalYear) ?? null;

  /* No year asked for: the most recently approved one. Sorted on the approval
     date rather than the fiscal-year string, because the string is Bikram
     Sambat and nothing here does that arithmetic. */
  return forDistrict.slice().sort((a, b) => (a.approvedOn < b.approvedOn ? 1 : -1))[0] ?? null;
}

/** Whether a treatment could ever be priced. What a screen greys out. */
export function isRatable(code: string): boolean {
  const t = treatmentFor(code, null);
  return t !== null && t.quantityKey !== null;
}

/* --------------------------- pasted from a spreadsheet --------------------------- */

/**
 * Rate rows out of pasted text — tab- or comma-separated, header optional.
 *
 * WHAT ACTUALLY EXISTS IN A DIVISION OFFICE IS A SPREADSHEET, and the shortest
 * path from it to here is the clipboard. So this accepts what a paste produces:
 * tabs from a spreadsheet, commas from a CSV, a header row or none, and blank
 * lines wherever somebody left them.
 *
 * BAD LINES ARE RETURNED, NOT SKIPPED. A parser that quietly drops the three
 * rows it could not read produces a table that loads cleanly and prices three
 * fewer items than the Division approved — and the estimate would report those
 * as "the approved rate table does not price this item", which is a true
 * sentence about a false table. Every unreadable line comes back with its
 * number.
 */
export function parseRateRows(text: string): { rows: RateRow[]; faults: string[] } {
  const rows: RateRow[] = [];
  const faults: string[] = [];

  const lines = text.split(/\r?\n/);
  for (const [i, raw] of lines.entries()) {
    const line = raw.trim();
    if (line === '') continue;

    const cells = line.split(/\t|,/).map((c) => c.trim());

    /* A header, recognised by its first cell rather than by position — somebody
       pasting a selection may not include the top row, and somebody pasting the
       whole sheet will. Skipped silently because it is not a fault. */
    if (i === 0 && /^treatment$/i.test(cells[0] ?? '')) continue;

    if (cells.length < 4) {
      faults.push(`Line ${i + 1}: expected treatment, unit, rate, normRef — got ${cells.length} value${cells.length === 1 ? '' : 's'}.`);
      continue;
    }

    const [treatment, unit, rateText, ...rest] = cells;
    const rate = Number(rateText);
    if (!Number.isFinite(rate)) {
      faults.push(`Line ${i + 1}: "${rateText}" is not a rate.`);
      continue;
    }

    /* The reference may itself contain a comma — "Norms 2075, item 15.4" — so
       everything after the rate is the reference rather than only the next
       cell. Splitting it would silently truncate the one field whose whole job
       is to be checkable against a printed document. */
    rows.push({
      treatment: treatment ?? '',
      unit: unit ?? '',
      rate,
      normRef: rest.join(', ').trim(),
    });
  }

  return { rows, faults };
}
